import { useState, useCallback, useRef, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useReactFlow } from '@xyflow/react'
import type { AIOperation, MindMapNode } from '@bandos/shared'
import { useMindMapContext } from '../../../../context/mindmap'
import { useAICallbacks } from '../../../../context/aiGeneration'
import { useSaveQueue } from '../../../../hooks/useSaveQueue'
import { updateNodeField } from '../../../../utils/nodeUpdateUtils'
import type { NodeChatMessage, NodeChatToolCall } from './types'
import { sendNodeChatMessage } from './nodeChatService'
import type { NodeChatResponse } from './nodeChatService'
import { filterValidToolCalls, validateToolCall } from './toolCallValidation'
import {
  markGenerating,
  clearGenerating,
  isNodeGenerating,
  onRequestComplete,
  notifyRequestComplete,
} from './nodeChatRequestTracker'
import { createSuggestions } from '../../utils/aiSuggestions'
import type { ApiResponse } from '@bandos/shared'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/** Persisted shape for chat history entries */
interface PersistedMessage {
  role: 'user' | 'ai'
  content: string
  timestamp: string
  toolCalls?: NodeChatToolCall[]
  suggestionIds?: string[]
}

function fromPersisted(entry: PersistedMessage): NodeChatMessage {
  return {
    id: `${entry.timestamp}-${Math.random().toString(36).slice(2, 6)}`,
    content: entry.content,
    sender: entry.role,
    timestamp: new Date(entry.timestamp),
    toolCalls: entry.toolCalls,
    suggestionIds: entry.suggestionIds,
  }
}

function toPersisted(msg: NodeChatMessage): PersistedMessage {
  return {
    role: msg.sender,
    content: msg.content,
    timestamp: msg.timestamp.toISOString(),
    ...(msg.toolCalls && msg.toolCalls.length > 0 ? { toolCalls: msg.toolCalls } : {}),
    ...(msg.suggestionIds && msg.suggestionIds.length > 0
      ? { suggestionIds: msg.suggestionIds }
      : {}),
  }
}

/** Text fields per node category that may cross-reference each other */
const TEXT_FIELDS: Record<string, string[]> = {
  venture: [
    'label',
    'companyName',
    'businessModelAndValueProp',
    'capabilitiesAndAssets',
    'hardConstraints',
    'goals',
    'productType',
  ],
  need: ['label'],
  idea: ['label'],
  information: ['label'],
}

export interface UseNodeChatReturn {
  messages: NodeChatMessage[]
  isLoading: boolean
  sendMessage: (text: string) => void
  autoGenerate: () => void
  approveToolCall: (messageId: string, toolIndex: number) => void
  rejectToolCall: (messageId: string, toolIndex: number) => void
  resetChat: () => void
  injectSuggestionMessage: (content: string, suggestionIds: string[]) => void
}

export function useNodeChat(nodeId: string): UseNodeChatReturn {
  const { getNode } = useReactFlow()
  const { setNodes, setHasUnsavedChanges, mindmapId } = useMindMapContext()
  const aiCallbacks = useAICallbacks()
  const saveQueue = useSaveQueue()

  const [messages, setMessages] = useState<NodeChatMessage[]>(() => {
    const node = getNode(nodeId)
    if (!node) return []
    const history = node.data.chatHistory as PersistedMessage[] | undefined
    return history ? history.map(fromPersisted) : []
  })

  const [isLoading, setIsLoading] = useState(() => isNodeGenerating(nodeId))
  const mountedRef = useRef(true)
  const lastPersistedJsonRef = useRef<string>('')
  // Stable ref for latest messages — used in callbacks to avoid stale closures
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  // Persist messages to node data whenever they change
  useEffect(() => {
    if (messages.length === 0) return
    const persisted = messages.map(toPersisted)
    const json = JSON.stringify(persisted)
    if (json === lastPersistedJsonRef.current) return
    lastPersistedJsonRef.current = json
    setNodes((nodes) => updateNodeField(nodes, nodeId, 'chatHistory', persisted))
    setHasUnsavedChanges(true)
  }, [messages, nodeId, setNodes, setHasUnsavedChanges])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Subscribe to in-flight request completion for the remount case:
  // If this node has an active generation (started by a previous component
  // instance before it unmounted), subscribe so we receive the AI message
  // and can update local state when the response arrives.
  useEffect(() => {
    if (!isNodeGenerating(nodeId)) return undefined

    return onRequestComplete(nodeId, (aiMessage) => {
      setMessages((prev) => {
        const next = [...prev, aiMessage]
        // Eagerly sync the persistence ref so the chatHistory effect
        // doesn't redundantly write the same data that the unmounted
        // handler already persisted to node data.
        lastPersistedJsonRef.current = JSON.stringify(next.map(toPersisted))
        return next
      })
      setIsLoading(false)
    })
  }, [nodeId])

  /** Shared response handler for both sendMessage and autoGenerate.
   *  Always processes the response regardless of component mount status. */
  const handleChatResponse = useCallback(
    (result: ApiResponse<NodeChatResponse>): void => {
      clearGenerating(nodeId)

      if (result.error) {
        console.error('[NodeChat] API error:', result.error)
      }

      const isOverloaded =
        result.error?.code === 'AI_SERVICE_OVERLOADED' || result.error?.status === 503

      // Filter to suggest_edit tool calls only (generate_suggestions is executed server-side)
      const responseToolCalls: NodeChatToolCall[] = filterValidToolCalls(
        result.data?.toolCalls ?? []
      ).map((tc) => ({
        name: tc.name,
        args: tc.args,
        status: 'pending' as const,
      }))

      // Handle server-side generated suggestions
      const genSuggestions = result.data?.generatedSuggestions
      let suggestionIds: string[] | undefined

      if (genSuggestions && genSuggestions.nodes.length > 0) {
        // Create local AISuggestion objects and add to the store
        const localSuggestions = createSuggestions(
          genSuggestions.nodes,
          genSuggestions.edges,
          genSuggestions.batchId,
          genSuggestions.suggestionIds
        )

        if (localSuggestions.length > 0) {
          const feedbackEntry = genSuggestions.feedback?.trim()

          // Add feedback to each suggestion if provided
          const suggestionsWithFeedback = feedbackEntry
            ? localSuggestions.map((s) => ({ ...s, userFeedback: feedbackEntry }))
            : localSuggestions

          // Add to canonical AI state so toggleSuggestion can find them.
          // The sync effect in useAIOperations propagates to AIStateStore automatically.
          if (aiCallbacks) {
            const childCategory = genSuggestions.nodes[0]?.data?.category as string | undefined
            const categoryToOp: Record<string, AIOperation> = {
              need: 'generate_needs_from_venture',
              idea: 'generate_idea',
            }
            const operation = categoryToOp[childCategory ?? ''] ?? ('generate_idea' as AIOperation)

            const parentNode = getNode(nodeId) as MindMapNode | undefined
            const sourceNode = parentNode ?? genSuggestions.nodes[0]

            aiCallbacks.addChatSuggestions(nodeId, suggestionsWithFeedback, {
              node: sourceNode,
              operation,
              feedbackEntry: feedbackEntry ?? undefined,
            })
          }
        }

        // Store IDs so the message renders suggestion bubbles
        suggestionIds = localSuggestions.map((s) => s.id)
      }

      const aiMessage: NodeChatMessage = {
        id: generateId(),
        content: isOverloaded
          ? 'The AI is currently busy. Please wait a moment and try again.'
          : (result.data?.response ?? 'Sorry, something went wrong. Please try again.'),
        sender: 'ai',
        timestamp: new Date(),
        ...(responseToolCalls.length > 0 ? { toolCalls: responseToolCalls } : {}),
        ...(suggestionIds && suggestionIds.length > 0 ? { suggestionIds } : {}),
      }

      if (mountedRef.current) {
        // Component still mounted — update local state normally
        setMessages((p) => [...p, aiMessage])
        setIsLoading(false)
      } else {
        // Component unmounted (node scrolled out of view) — persist directly
        // to node data so the response isn't lost. setNodes is from MindMapContext
        // whose provider lives above React Flow and remains valid after unmount.
        // setIsLoading is called unconditionally: it's a no-op when truly unmounted
        // but guards against StrictMode double-invocation flipping mountedRef early.
        setIsLoading(false)
        setNodes((nodes) => {
          const node = nodes.find((n) => n.id === nodeId)
          const existing = (node?.data.chatHistory ?? []) as PersistedMessage[]
          return updateNodeField(nodes, nodeId, 'chatHistory', [
            ...existing,
            toPersisted(aiMessage),
          ])
        })
        setHasUnsavedChanges(true)
        // Notify a remounted component instance (if one has subscribed)
        notifyRequestComplete(nodeId, aiMessage)
      }
    },
    [nodeId, aiCallbacks, getNode, setNodes, setHasUnsavedChanges]
  )

  const handleChatError = useCallback(
    (err: unknown): void => {
      clearGenerating(nodeId)
      console.error('[NodeChat] Unhandled error:', err)
      Sentry.captureException(err, {
        tags: { feature: 'mindmap.nodechat', action: 'send_message' },
      })
      const aiMessage: NodeChatMessage = {
        id: generateId(),
        content: 'Sorry, something went wrong. Please try again.',
        sender: 'ai',
        timestamp: new Date(),
      }

      if (mountedRef.current) {
        setMessages((p) => [...p, aiMessage])
        setIsLoading(false)
      } else {
        setIsLoading(false)
        setNodes((nodes) => {
          const node = nodes.find((n) => n.id === nodeId)
          const existing = (node?.data.chatHistory ?? []) as PersistedMessage[]
          return updateNodeField(nodes, nodeId, 'chatHistory', [
            ...existing,
            toPersisted(aiMessage),
          ])
        })
        setHasUnsavedChanges(true)
        notifyRequestComplete(nodeId, aiMessage)
      }
    },
    [nodeId, setNodes, setHasUnsavedChanges]
  )

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || !mindmapId) return

      const userMessage: NodeChatMessage = {
        id: generateId(),
        content: trimmed,
        sender: 'user',
        timestamp: new Date(),
      }

      // Build history from the ref (latest state) to avoid stale closure
      const history = messagesRef.current
        .filter((msg) => msg.content.length > 0)
        .map((msg) => ({
          role: msg.sender,
          content: msg.content,
        }))

      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)
      markGenerating(nodeId)

      const sidebarFeedback = aiCallbacks?.getFeedbackHistory(nodeId)

      void sendNodeChatMessage(mindmapId, nodeId, trimmed, history, sidebarFeedback)
        .then(handleChatResponse)
        .catch(handleChatError)
    },
    [mindmapId, nodeId, aiCallbacks, handleChatResponse, handleChatError]
  )

  /** Auto-generate: flushes pending save, then triggers generation without a user message bubble */
  const autoGenerate = useCallback(() => {
    if (!mindmapId) return

    setIsLoading(true)
    markGenerating(nodeId)

    // Flush the debounced save so the backend can find the newly-created node
    void saveQueue
      .waitForPendingSaves()
      .then(() => {
        const sidebarFeedback = aiCallbacks?.getFeedbackHistory(nodeId)
        return sendNodeChatMessage(mindmapId, nodeId, 'generate', [], sidebarFeedback, true)
      })
      .then(handleChatResponse)
      .catch(handleChatError)
  }, [mindmapId, nodeId, aiCallbacks, saveQueue, handleChatResponse, handleChatError])

  // Auto-generate on mount if the node was just created from accepting a suggestion
  const autoGenerateTriggeredRef = useRef(false)
  useEffect(() => {
    if (autoGenerateTriggeredRef.current) return
    const node = getNode(nodeId)
    if (!node?.data?.pendingChatGeneration) return
    autoGenerateTriggeredRef.current = true
    setNodes((nodes) => updateNodeField(nodes, nodeId, 'pendingChatGeneration', false))
    autoGenerate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Approve a tool call — apply the edit (suggest_edit only) */
  const approveToolCall = useCallback(
    (messageId: string, toolIndex: number) => {
      // Read tool call data from the ref BEFORE state update to avoid stale closure
      const msg = messagesRef.current.find((m) => m.id === messageId)
      const tc = msg?.toolCalls?.[toolIndex]
      if (!tc) return

      // Re-validate before applying — guard against malformed args slipping through
      const validation = validateToolCall(tc)
      if (!validation.valid) {
        console.warn('[NodeChat] Blocked invalid tool call on approve:', validation.error, tc)
        return
      }

      // Mark as approved in state
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.toolCalls) return m
          const updatedCalls = m.toolCalls.map((call, i) =>
            i === toolIndex ? { ...call, status: 'approved' as const } : call
          )
          return { ...m, toolCalls: updatedCalls }
        })
      )

      if (tc.name !== 'suggest_edit') return

      const field = tc.args.field
      const newValue = tc.args.value

      setNodes((nodes) => {
        const node = nodes.find((n) => n.id === nodeId)
        if (!node) return updateNodeField(nodes, nodeId, field, newValue, undefined, true)

        const data = node.data as Record<string, unknown>
        const oldValue = (data[field] as string) ?? ''

        let updated = updateNodeField(nodes, nodeId, field, newValue, undefined, true)

        // Propagate: replace old value in other text fields on this node
        if (oldValue && oldValue.length >= 2) {
          const category = data.category as string
          const fieldsToCheck = (TEXT_FIELDS[category] ?? []).filter((f) => f !== field)

          for (const otherField of fieldsToCheck) {
            const otherValue = (data[otherField] as string) ?? ''
            if (otherValue && otherValue.includes(oldValue)) {
              const replaced = otherValue.replaceAll(oldValue, newValue)
              updated = updateNodeField(updated, nodeId, otherField, replaced, undefined, true)
            }
          }
        }

        return updated
      })
      setHasUnsavedChanges(true)
    },
    [nodeId, setNodes, setHasUnsavedChanges]
  )

  const rejectToolCall = useCallback((messageId: string, toolIndex: number) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId || !msg.toolCalls) return msg
        const updatedCalls = msg.toolCalls.map((tc, i) =>
          i === toolIndex ? { ...tc, status: 'rejected' as const } : tc
        )
        return { ...msg, toolCalls: updatedCalls }
      })
    )
  }, [])

  const resetChat = useCallback(() => {
    clearGenerating(nodeId)
    setMessages([])
    lastPersistedJsonRef.current = ''
    setNodes((nodes) => updateNodeField(nodes, nodeId, 'chatHistory', []))
    setHasUnsavedChanges(true)
  }, [nodeId, setNodes, setHasUnsavedChanges])

  const injectSuggestionMessage = useCallback((content: string, suggestionIds: string[]) => {
    const msg: NodeChatMessage = {
      id: generateId(),
      content,
      sender: 'ai',
      timestamp: new Date(),
      suggestionIds,
    }
    setMessages((prev) => [...prev, msg])
  }, [])

  return {
    messages,
    isLoading,
    sendMessage,
    autoGenerate,
    approveToolCall,
    rejectToolCall,
    resetChat,
    injectSuggestionMessage,
  }
}
